import type { CSSProperties, JSX, ReactNode } from "react";
import { cx } from "../cx";

export type StatusPillTone = "blue" | "green" | "gray" | "amber" | "red" | "volt";

export type StatusPillEmphasis = "toned" | "quiet";

export interface StatusPillProps {
  /**
   * Lifecycle tone, mapped strictly to the theme-aware --tone-<tone>-ink/wash
   * pairs: blue = admitted/in-flight, green = succeeded, gray = observed/
   * derived, amber = hold/needs-attention, red = failed/violated. `volt` is
   * reserved for verified/live states — volt stays earned; never use it for
   * generic emphasis.
   */
  tone?: StatusPillTone | undefined;
  /** Pill text ("Admitted", "Succeeded"). */
  label?: ReactNode | undefined;
  /** Leading dot in the tone's ink color. */
  dot?: boolean | undefined;
  /**
   * `toned` washes the pill for states that demand attention; `quiet`
   * drops the wash for neutral lifecycle states (derived, observed,
   * authorized) so toned pills keep their signal value.
   */
  emphasis?: StatusPillEmphasis | undefined;
  className?: string | undefined;
  style?: CSSProperties | undefined;
}

/**
 * Lifecycle state pill for activity and data views. Colors come only from
 * the --tone-*-ink/wash pairs, so pills adapt under data-theme="dark".
 * Ported from design/tarski-kit/components/studio/StatusPill.jsx.
 */
export function StatusPill({
  tone = "gray",
  label,
  dot = false,
  emphasis = "toned",
  className,
  style,
}: StatusPillProps): JSX.Element {
  return (
    <span
      className={cx(
        "tk-status-pill",
        `tk-status-pill--${tone}`,
        emphasis === "quiet" && "tk-status-pill--quiet",
        className,
      )}
      style={style}
    >
      {dot ? <span className="tk-status-pill__dot" aria-hidden="true" /> : null}
      {label}
    </span>
  );
}
